
import React from 'react';

interface HangersAndBagsProps {
  hangerCount: number;
  setHangerCount: (count: number) => void;
  bagCount: number;
  setBagCount: (count: number) => void;
  useOwnHanger: boolean;
  setUseOwnHanger: (use: boolean) => void;
  useOwnBag: boolean;
  setUseOwnBag: (use: boolean) => void;
  getHangerCost: () => number;
  getBagCost: () => number;
}

/**
 * Component handling selection of hangers and bags.
 * Includes robust guards against invalid counts and provides clear UI feedback.
 */
export default function HangersAndBags({
  hangerCount,
  setHangerCount,
  bagCount,
  setBagCount,
  useOwnHanger,
  setUseOwnHanger,
  useOwnBag,
  setUseOwnBag,
  getHangerCost,
  getBagCost,
}: HangersAndBagsProps) {
  // ----- Helper functions ---------------------------------------------------
  /** Safely update hanger count, never allowing a negative value. */
  const updateHangerCount = (newCount: number) => {
    const safeCount = Math.max(0, Math.floor(newCount));
    setHangerCount(safeCount);
  };

  /** Safely update bag count, never allowing a negative value. */
  const updateBagCount = (newCount: number) => {
    const safeCount = Math.max(0, Math.floor(newCount));
    setBagCount(safeCount);
  };

  // ----- Render -------------------------------------------------------------
  return (
    <div className="bg-teal-50 border border-teal-100 rounded-xl sm:rounded-2xl p-3 sm:p-6 mt-4 sm:mt-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-3 sm:mb-5">
        <h2 className="text-base sm:text-xl font-bold flex items-center gap-2">
          <i className="ri-hanger-line text-teal-600 text-lg sm:text-xl" />
          Ramínka a obaly
        </h2>
        <span className="bg-teal-100 text-teal-700 text-[10px] sm:text-xs rounded-full flex items-center gap-1 font-medium px-1.5 sm:px-2 py-0.5 sm:py-1">
          <i className="ri-leaf-fill text-xs sm:text-sm" />
          <span className="hidden sm:inline">Vlastní = ZDARMA</span>
          <span className="sm:hidden">ZDARMA</span>
        </span>
      </div>

      {/* Grid container - stack on mobile */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-6">
        {/* ---------- Hangers ---------- */}
        <div className="bg-white rounded-lg sm:rounded-xl p-3 sm:p-5 shadow-sm">
          <div className="mb-2 sm:mb-4">
            <span className="font-semibold text-gray-800 text-sm sm:text-base">Ramínka:</span>
          </div>

          {/* Selection buttons - vertical on mobile */}
          <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-2 sm:gap-3">
            {/* Own hanger */}
            <button
              type="button"
              onClick={() => {
                setUseOwnHanger(true);
                updateHangerCount(0);
              }}
              className={`px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm font-semibold flex items-center justify-center gap-2 transition-all cursor-pointer ${
                useOwnHanger
                  ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-lg ring-2 ring-green-400'
                  : 'bg-green-50 text-green-700 hover:bg-green-100 border-2 border-green-200 hover:border-green-400'
              }`}
            >
              <i className={`ri-leaf-fill ${useOwnHanger ? 'text-white' : 'text-green-600'}`} />
              <span>Vlastní ramínko</span>
              {useOwnHanger && <i className="ri-check-line text-base sm:text-lg" />}
            </button>

            <span className="text-gray-400 text-center hidden sm:block">→</span>

            {/* Rental hanger */}
            <button
              type="button"
              onClick={() => {
                setUseOwnHanger(false);
                if (hangerCount === 0) updateHangerCount(1);
              }}
              className={`px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm font-medium transition-all cursor-pointer ${
                !useOwnHanger
                  ? 'bg-pink-100 text-pink-700 ring-2 ring-pink-300'
                  : 'bg-gray-100 text-gray-600 hover:bg-pink-50'
              }`}
            >
              Ramínko (5 Kč/ks)
            </button>
          </div>

          {/* Quantity selector for rented hangers */}
          {!useOwnHanger && (
            <div className="flex items-center gap-2 sm:gap-3 mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-gray-100">
              <span className="text-xs sm:text-sm text-gray-600">Počet:</span>
              <div className="flex items-center gap-0.5 bg-gray-100 rounded-lg">
                <button
                  type="button"
                  onClick={() => updateHangerCount(hangerCount - 1)}
                  className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-l-lg text-gray-600 hover:bg-gray-200 font-bold cursor-pointer"
                >
                  −
                </button>
                <span className="w-7 sm:w-8 text-center font-bold text-gray-900 text-sm sm:text-base">{hangerCount}</span>
                <button
                  type="button"
                  onClick={() => updateHangerCount(hangerCount + 1)}
                  className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-r-lg text-gray-600 hover:bg-gray-200 font-bold cursor-pointer"
                >
                  +
                </button>
              </div>
              {hangerCount > 0 && (
                <span className="text-teal-600 font-bold text-sm sm:text-lg ml-auto">{getHangerCost()} Kč</span>
              )}
            </div>
          )}

          {/* Message for own hanger */}
          {useOwnHanger && (
            <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-gray-100">
              <div className="flex items-center gap-2 text-green-600 text-xs sm:text-sm bg-green-50 p-2 rounded-lg">
                <i className="ri-recycle-fill text-base sm:text-lg" />
                <span className="font-medium">
                  Ekologická volba! 🌍
                </span>
              </div>
            </div>
          )}
        </div>

        {/* ---------- Bags ---------- */}
        <div className="bg-white rounded-lg sm:rounded-xl p-3 sm:p-5 shadow-sm">
          <div className="mb-2 sm:mb-4">
            <span className="font-semibold text-gray-800 text-sm sm:text-base">Obaly:</span>
          </div>

          {/* Selection buttons - vertical on mobile */}
          <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-2 sm:gap-3">
            {/* Own bag */}
            <button
              type="button"
              onClick={() => {
                setUseOwnBag(true);
                updateBagCount(0);
              }}
              className={`px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm font-semibold flex items-center justify-center gap-2 transition-all cursor-pointer ${
                useOwnBag
                  ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-lg ring-2 ring-green-400'
                  : 'bg-green-50 text-green-700 hover:bg-green-100 border-2 border-green-200 hover:border-green-400'
              }`}
            >
              <i className={`ri-leaf-fill ${useOwnBag ? 'text-white' : 'text-green-600'}`} />
              <span>Vlastní obal</span>
              {useOwnBag && <i className="ri-check-line text-base sm:text-lg" />}
            </button>

            <span className="text-gray-400 text-center hidden sm:block">→</span>

            {/* Rental bag */}
            <button
              type="button"
              onClick={() => {
                setUseOwnBag(false);
                if (bagCount === 0) updateBagCount(1);
              }}
              className={`px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm font-medium transition-all cursor-pointer ${
                !useOwnBag
                  ? 'bg-pink-100 text-pink-700 ring-2 ring-pink-300'
                  : 'bg-gray-100 text-gray-600 hover:bg-pink-50'
              }`}
            >
              Igelitový (15 Kč)
            </button>
          </div>

          {/* Quantity selector for rented bags */}
          {!useOwnBag && (
            <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-gray-100">
              <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                <span className="text-xs sm:text-sm text-gray-600">Počet:</span>
                <div className="flex items-center gap-0.5 bg-gray-100 rounded-lg">
                  <button
                    type="button"
                    onClick={() => updateBagCount(bagCount - 1)}
                    className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-l-lg text-gray-600 hover:bg-gray-200 font-bold cursor-pointer"
                  >
                    −
                  </button>
                  <span className="w-7 sm:w-8 text-center font-bold text-gray-900 text-sm sm:text-base">{bagCount}</span>
                  <button
                    type="button"
                    onClick={() => updateBagCount(bagCount + 1)}
                    className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-r-lg text-gray-600 hover:bg-gray-200 font-bold cursor-pointer"
                  >
                    +
                  </button>
                </div>
                {bagCount > 0 && (
                  <span className="text-teal-600 font-bold text-sm sm:text-lg ml-auto">{getBagCost()} Kč</span>
                )}
              </div>
              <p className="text-[10px] sm:text-xs text-gray-500 flex items-center gap-1">
                <i className="ri-recycle-line text-teal-500" />
                Znovupoužitelný (89 Kč)
              </p>
            </div>
          )}

          {/* Message for own bag */}
          {useOwnBag && (
            <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-gray-100">
              <div className="flex items-center gap-2 text-green-600 text-xs sm:text-sm bg-green-50 p-2 rounded-lg">
                <i className="ri-recycle-fill text-base sm:text-lg" />
                <span className="font-medium">
                  Ekologická volba! 🌍
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
